import classNames from "classnames";
import { Question } from "components/Question/Question";
import { QuestionInput } from "components/QuestionInput/QuestionInput";
import { TextTyping } from "components/QuestionInput/TextTyping/TextTyping";
import { QuestionMedia } from "components/QuestionMedia/QuestionMedia";
import { MEDIA_TYPE, QUESTION_TYPE } from "constant";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import MCQWithImages from "./MCQWithImages";
import { TextCompletion } from "./TextCompletion";
import { useIsMobileDevice, useScrollDown } from "utils/hooks";
import { FaArrowCircleDown } from "react-icons/fa";
import "./index.css"

// fullWidth - Only for desktop view with images + mobile view
const SpeechInstructionBubble = ({ type, fullWidth = true }) => {
  return (
    <>
      {
        !![QUESTION_TYPE.SPEECH_SCRIPTED, QUESTION_TYPE.SPEECH_SPONTANEOUS].includes(type) ? (
          <div className="flex items-center justify-center mb-4">
            <div className={classNames(
              "mb-2 text-center text-base font-normal p-4 sm:w-full bg-wizr-grey rounded-lg",
              { "sm:w-1/2": !fullWidth }
            )}>
              <p>
                <span className="text-cocoa-brown font-semibold text-lg">Instructions: </span>
                {type === QUESTION_TYPE.SPEECH_SCRIPTED ?
                  'Record yourself saying the statement below' :
                  'Record your answer to the question below'}
              </p>
            </div>
          </div>
        ) : <></>
      }
    </>
  )
};

export const QuizBody = ({
  id: questionId,
  question,
  options,
  type,
  media = [],
  audioTranscript = "",
  optionSubmitHandler,
  time,
  timeToSolve,
  isOffline,
  randomizeOptions,
  setPauseTimer,
}) => {
  const [containerHeight, setContainerHeight] = useState(0);
  const [mediaDimensions, setMediaDimensions] = useState({
    height: 0,
    width: 0,
  });
  const [isQuesTextDocked, setIsQuesTextDocked] = useState(false)
  const [isQuesInputDocked, setIsQuesInputDocked] = useState(false)
  const [isMobileNextBtnVisible, setIsMobileNextBtnVisible] = useState(false)
  const containerRef = useRef(null);
  const quesTextContainer = useRef(null)
  const quesInputContainer = useRef(null)
  const { isScrollDownOptionsBtnVisible, handleScrollDownOptions } = useScrollDown({ overflowElement: containerRef?.current, showBtn: !isQuesInputDocked || !isQuesTextDocked })
  const isMobile = useIsMobileDevice()
  const mediaSize = (mediaDimensions.height / containerHeight) * 100;

  function mediaLoadHandler(event) {
    if (event) {
      const img = event.target;
      setMediaDimensions({
        height: img.naturalHeight,
        width: img.naturalWidth,
      });
    }
  }

  const isOptionText = useCallback(
    () => options.some((option) => option.type === "text"),
    [options]
  );

  const isOptionTextLong = useCallback(
    () =>
      options.some(
        (option) => option.type === "text" && option.text?.length > 40
      ),
    [options]
  );

  const handleContainerScroll = useCallback(() => {
    if (quesTextContainer?.current?.clientHeight > containerRef?.current?.clientHeight) {
      if (containerRef?.current?.scrollTop >= containerRef?.current.clientHeight * 0.3) {
        setIsMobileNextBtnVisible(true)
      }
    } else {
      setIsMobileNextBtnVisible(true)
    }
  }, [quesTextContainer?.current?.clientHeight, containerRef?.current?.clientHeight])

  const isSpeech = useMemo(
    () =>
      [
        QUESTION_TYPE.SPEECH_SCRIPTED,
        QUESTION_TYPE.SPEECH_SPONTANEOUS,
      ].includes(type),
    [type]
  );

  const isQuestionInFirstColumn =
    !(
      // Making question and mic appear in same column
      // !media check for AI-generated questions
      ((!media || media?.length === 0) && isSpeech)
    ) &&
    ((media?.length > 0 && isOptionTextLong()) ||
      (!media?.length && isOptionTextLong()) ||
      question.length ||
      isOptionTextLong() ||
      (type === QUESTION_TYPE.FREE_TEXT && media?.length > 0));

  // boolean value to decide if the question is an mcq and options are of type images
  const isQuesMCQWithImageOptions = useMemo(() => type === QUESTION_TYPE.MCQ && options[0].type === MEDIA_TYPE.IMAGE, [type, options])

  useEffect(() => {
    if (containerRef.current) {
      setContainerHeight(containerRef.current.clientHeight);
    }
    // scrolling to top on next question
    window.scrollTo(0, 0);

    containerRef?.current?.addEventListener("scroll", handleContainerScroll)
    return () => containerRef?.current?.removeEventListener("scroll", handleContainerScroll)
  }, []);

  useEffect(() => {
    if (quesTextContainer?.current?.clientHeight < containerRef?.current?.clientHeight) {
      setIsQuesTextDocked(true)
    } else {
      setIsQuesTextDocked(false)
    }
    if (quesInputContainer?.current?.clientHeight < containerRef?.current?.clientHeight) {
      setIsQuesInputDocked(true)
    } else {
      setIsQuesInputDocked(false)
    }
  }, [quesTextContainer?.current?.clientHeight, quesInputContainer?.current?.clientHeight, containerRef?.current?.clientHeight])

  useEffect(() => {
    handleContainerScroll()
  }, [containerRef?.current?.clientHeight, quesTextContainer?.current?.clientHeight])

  if (type === QUESTION_TYPE.TYPING_TEST) {
    const [firstMedia] = media ?? [];
    const isMediaAudioType =
      firstMedia && firstMedia.mediaType === MEDIA_TYPE.AUDIO;
    return (
      <div
        className={classNames(
          "flex h-[calc(100%-44px)] items-center justify-center px-5",
          "sm:mt-14 sm:h-[calc(100%-150px)] sm:px-36"
        )}
      >
        <TextTyping
          text={isMediaAudioType ? audioTranscript : question}
          heading={question}
          onComplete={optionSubmitHandler}
          audio={isMediaAudioType ? firstMedia : null}
          autoSubmitOnCompletion={!isMediaAudioType}
          time={time}
        />
      </div>
    );
  }

  if (type === QUESTION_TYPE.TEXT_COMPLETION) {
    return (
      <div
        className={classNames(
          "flex h-[calc(100%-44px)] items-center justify-center px-5",
          "sm:mt-14 sm:h-[calc(100%-150px)] sm:px-36"
        )}
      >
        <TextCompletion text={question} onComplete={optionSubmitHandler} time={time} />
      </div>
    );
  }

  return isQuesMCQWithImageOptions
    ? (
      <MCQWithImages
        ref={containerRef}
        question={question}
        questionId={questionId}
        type={type}
        media={media}
        options={options}
        optionSubmitHandler={optionSubmitHandler}
        mediaLoadHandler={mediaLoadHandler}
        isOptionTextLong={isOptionTextLong}
        isQuesMCQWithImageOptions={isQuesMCQWithImageOptions}
        time={time}
        randomizeOptions={randomizeOptions}
      />
    )
    : (
      <div
        className={classNames(
          "flex flex-col justify-between px-[18px] pb-[26px] pt-4 h-[90vh] overflow-y-auto",
          "sm:h-[calc(100%-140px)] sm:flex-row sm:gap-16 sm:p-0 sm:px-16",
          {
            "sm:mt-14": (!!media?.length || isQuestionInFirstColumn) && type !== QUESTION_TYPE.FREE_TEXT,
            "sm:justify-center": !media?.length,
          },
          "xl:gap-32 xl:p-0 xl:px-36",
          "sm:h-[calc(100%-132px)] short:sm:mt-7",
          {
            "0xl:gap-20 xl:p-0 xl:px-8 short:sm:mt-0 sm:mt-0 sm:gap-20 sm:p-0 sm:px-8": type === QUESTION_TYPE.FREE_TEXT
          }
        )}
        ref={containerRef}
      >
        {(!!media?.length || isQuestionInFirstColumn) && (
          <div
            ref={quesTextContainer}
            className={`${isQuesTextDocked && !isMobile ? "sticky top-0" : ""} flex flex-col sm:h-fit ques-scroll ${type === QUESTION_TYPE.FREE_TEXT ? "sm:flex-[0.85]" : "sm:flex-1"} sm:w-[50%]`}
          >
            <SpeechInstructionBubble type={type} />
            {isQuestionInFirstColumn && (
              <div className="order-2 sm:order-1 sm:mb-5">
                <Question text={question} type={type} noMedia={!media?.length} />
              </div>
            )}
            <div className="order-1 sm:order-2">
              <QuestionMedia
                media={media}
                onMediaLoad={mediaLoadHandler}
                questionLength={question.length}
                containerHeight={containerHeight}
                isWidthAuto={mediaSize > 80 || isQuestionInFirstColumn}
              />
            </div>
          </div>
        )}
        <div
          ref={quesInputContainer}
          className={classNames(`sm:h-fit sm:flex-1 ${isQuesInputDocked && !isMobile ? "sticky top-0" : ""}`, {
            "lg:w-full": type === QUESTION_TYPE.FREE_TEXT,
            "sm:w-1/2 sm:flex-initial sm:flex-col sm:justify-center":
              !media?.length && !isQuestionInFirstColumn && !isSpeech,
            "sm:flex-1 sm:flex-col sm:justify-center":
              media?.length === 0 && isSpeech && !isQuestionInFirstColumn,
          })}
        >

          {!isQuestionInFirstColumn && (
            <>
              <SpeechInstructionBubble type={type} fullWidth={false} />
              <div className={"mb-5"}>
                <Question text={question} type={type} noMedia={!media?.length} />
              </div>
            </>
          )}
          <QuestionInput
            type={type}
            questionId={questionId}
            showGridView={
              mediaSize >= 50 || !isOptionText() || options.length > 4
            }
            options={options}
            isOptionTextLong={isOptionTextLong}
            onSubmit={optionSubmitHandler}
            time={time}
            timeToSolve={type === QUESTION_TYPE.SPEECH_SPONTANEOUS ? timeToSolve : null}
            isOffline={isOffline}
            randomizeOptions={randomizeOptions}
            isNextBtnVisible={isMobileNextBtnVisible}
            setPauseTimer={setPauseTimer}
          />
          {
            isScrollDownOptionsBtnVisible && !isMobile
              ? <div className="w-full flex justify-end fixed right-10 bottom-20 p-2">
                <button onClick={handleScrollDownOptions} className="bounce-anim bg-transparent cursor-pointer">
                  <FaArrowCircleDown className="w-[30px] h-[30px]" fill={"#2e1500"} />
                </button>
              </div> : <></>
          }
        </div>
      </div>
    );
};
